import type { HTMLAttributes, ReactNode } from "react";

/**
 * Badge — pastille de statut (différent de `Tag` qui est éditorial avec ✕).
 *
 * Variantes sémantiques :
 *  - `success` (vine)       : conforme, validé, livré
 *  - `warning` (champagne)  : attention, échéance
 *  - `danger`  (rust)       : refusé, bloqué, alerte
 *  - `neutral` (slate)      : info neutre / désactivé
 *  - `info`    (aubergine)  : information métier (journal d'activité)
 */
export type BadgeVariant = "success" | "warning" | "danger" | "neutral" | "info";

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  /** Taille : `sm` (compact) / `md` (défaut, plus lisible). */
  size?: "sm" | "md";
  /** Affiche une pastille colorée devant le libellé. */
  dot?: boolean;
  children?: ReactNode;
}

// Couples couleur (bg + texte + bordure) alignés sur la palette `styles.css`.
const VARIANT_CLASSES: Record<BadgeVariant, string> = {
  success: "bg-vine-50 text-vine-800 border-vine-200",
  warning: "bg-champagne-50 text-champagne-800 border-champagne-300",
  danger: "bg-rust-50 text-rust-800 border-rust-200",
  neutral: "bg-slate-50 text-slate-700 border-slate-200",
  info: "bg-aubergine-50 text-aubergine-900 border-aubergine-200",
};

// Couleur du dot (full color) — un cran plus saturé que le texte.
const DOT_CLASSES: Record<BadgeVariant, string> = {
  success: "bg-vine-500",
  warning: "bg-champagne-500",
  danger: "bg-rust-500",
  neutral: "bg-slate-400",
  info: "bg-aubergine-500",
};

const SIZE_CLASSES: Record<NonNullable<BadgeProps["size"]>, string> = {
  sm: "px-1.5 py-0.5 text-[11px] gap-1",
  md: "px-2 py-0.5 text-[12px] gap-1.5",
};

export function Badge({
  variant = "neutral",
  size = "md",
  dot = false,
  className = "",
  children,
  ...rest
}: BadgeProps) {
  const composed = [
    "inline-flex items-center rounded-full border font-medium",
    VARIANT_CLASSES[variant],
    SIZE_CLASSES[size],
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span className={composed} {...rest}>
      {dot && (
        <span
          aria-hidden="true"
          className={["inline-block h-1.5 w-1.5 rounded-full", DOT_CLASSES[variant]].join(" ")}
        />
      )}
      {children}
    </span>
  );
}
